import { Button, Modal } from '@bfe/moly';
import { DirectionrightIcon } from '@bfe/moly-icons';
import { get, getResultFromResponse, runAsyncSafe } from '@web3/utils';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useMemoizedFn } from '../../hooks';
import { BizType, NotificationItem } from './types';

export type Web3LeaveNotificationProps = {
  bizType: BizType;
};
export const Web3LeaveNotification = ({ bizType }: Web3LeaveNotificationProps) => {
  const [visible, setVisible] = useState(false);
  const [notification, setNotification] = useState<NotificationItem | null>(null);

  const storageKey = `${STORAGE_KEY_PREFIX}${bizType}`;

  const saveShowStatus = useMemoizedFn((item: NotificationItem) => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        timestamp: Date.now(),
        version: item.version,
      }),
    );
  });

  const handleClose = useMemoizedFn(() => {
    setVisible(false);
  });

  useEffect(() => {
    const checkShouldShow = (item: NotificationItem): boolean => {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return true;

      const { timestamp, version } = JSON.parse(stored);
      const now = Date.now();

      // 如果版本不同，或者超过间隔时间，就显示
      return version !== item.version || now - timestamp > item.interval;
    };

    const fetchNotification = async () => {
      const [err, response] = await runAsyncSafe(getResultFromResponse(getNotificationList(bizType)));
      const item = response?.[0];
      if (item && checkShouldShow(item)) {
        setNotification(item);
        setVisible(true);
      } else {
        console.error('Failed to fetch notification:', err);
      }
    };

    fetchNotification();
  }, [bizType, storageKey]);

  useEffect(() => {
    if (visible && notification) {
      // 只要显示了，就保存状态
      saveShowStatus(notification);
    }
  }, [visible, notification, saveShowStatus]);

  if (!notification) return null;

  return (
    <Modal
      open={visible}
      onClose={handleClose}
      title={notification.title}
      footer={
        <div className="flex flex-col gap-4 items-center">
          {notification.actions?.map((action) => (
            <Button
              key={action.text}
              variant="primary"
              className="w-full"
              onClick={() => {
                if (action.url) {
                  window.open(action.url, '_blank');
                }
                handleClose();
              }}
            >
              {action.text}
            </Button>
          ))}
        </div>
      }
    >
      {notification.contents.map((content, index) => (
        <div key={index} className={clsx('text-bds-gray-t1-title', index !== 0 && 'mt-4')}>
          <h4 className="text-sm font-semibold">{content.subTitle}</h4>
          <p className="py-3 text-sm">{content.content}</p>
        </div>
      ))}

      {notification.link && (
        <div
          onClick={() => {
            notification.link && window.open(notification.link.url, '_blank');
          }}
          className="flex items-center gap-1 cursor-pointer text-bds-gray-t1-title text-xs"
        >
          <span>{notification.link.text}</span>
          <div className="svg-full size-3">
            <DirectionrightIcon />
          </div>
        </div>
      )}
    </Modal>
  );
};

const getNotificationList = (bizType: BizType) => {
  return get<NotificationItem[]>('/spot/api/web3/content/pop/list', {
    params: { bizType },
  });
};

const STORAGE_KEY_PREFIX = 'web3_pop_notification_';
